import { useEffect, useState } from "react";

/**
 * Per-user persisted state.
 *
 *   const [view, setView] = usePersistedState("customers.view", "grid");
 *
 * Keys are namespaced by the logged-in user's id so different reps on the
 * same browser don't share preferences. Falls back to a generic namespace
 * when no user is loaded yet.
 */
function userScope() {
  try {
    const raw = localStorage.getItem("hv:user");
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.id) return u.id;
    }
  } catch {
    /* noop */
  }
  return "_anon";
}

export function usePersistedState(key, initial) {
  const fullKey = () => `hv:${userScope()}:${key}`;

  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(fullKey());
      if (raw === null) return initial;
      const parsed = JSON.parse(raw);
      // Guard against corrupt/legacy entries where the stored shape no
      // longer matches the caller's `initial`.  e.g. an old `null` saved
      // under a key whose initial is `[]` would crash `.forEach()` /
      // `.filter()` chains on first render.
      if (Array.isArray(initial) && !Array.isArray(parsed)) return initial;
      if (
        initial !== null &&
        typeof initial === "object" &&
        !Array.isArray(initial) &&
        (parsed === null || typeof parsed !== "object" || Array.isArray(parsed))
      ) {
        return initial;
      }
      return parsed;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(fullKey(), JSON.stringify(value));
    } catch {
      /* quota exceeded — ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return [value, setValue];
}
